import { EOgqlJobState, IOgqlV1JobResult, IOgqlV1QueryResult } from '@opengov/ogql-types';
import {
  ColumnValueSegment,
  IAccountSummary,
  IDatasetChangeApplyResponse,
  IDatasetChangeMapping,
  IDatasetChangePlanResponse,
  IReport,
  IReportConfiguration
} from '@opengov/reporting-types';
import { transport } from '@opengov/transport';
import { v4 as uuidv4 } from 'uuid';

const platformApiHost = process.env.PLATFORM_API_HOST || '';
const platform = transport.url(platformApiHost);
const reportingService = platform.url('/api/reporting_service/v2');

const reportingServiceNext = platform.url('/api/reporting_service/next');

const reportingServiceV1 = platform.url('/api/reporting_service/v1');

// When the embed-service (and other backend callers) supply an in-cluster reporting-service URL,
// bypass the public PLATFORM_API_HOST edge so the call stays service-to-service. Browser callers
// pass nothing and keep the original module-level transport.
const reportingServiceNextOnHost = (reportingServiceHost?: string) =>
  reportingServiceHost ? transport.url(reportingServiceHost).url('/api/reporting_service/next') : reportingServiceNext;

const reportingServiceV1OnHost = (reportingServiceHost?: string) =>
  reportingServiceHost ? transport.url(reportingServiceHost).url('/api/reporting_service/v1') : reportingServiceV1;

export const createReport = async (report: IReport, clientOptions?: RequestInit): Promise<IReport> =>
  reportingService.options(clientOptions, true).url('/reports').post(report).json<IReport>();

export const getPivotCols = async (
  reportId: string,
  reqBody: IReportConfiguration,
  subdomain?: string,
  clientOptions?: RequestInit
): Promise<IOgqlV1JobResult> =>
  reportingService
    .options(clientOptions, true)
    .url(`/reports/${reportId}/pivot/columns`)
    .query({ entitySubdomain: subdomain })
    .post(reqBody)
    .json<IOgqlV1QueryResult>()
    .then((results) => {
      const trimmedData = results.data.map((value) => {
        if (value.length === 0) return [];
        const [s, ...rest] = value;
        return [s.trim(), ...rest];
      });
      return { id: uuidv4(), jobResult: { ...results, data: trimmedData }, state: EOgqlJobState.finished };
    });

export interface IGetPivotRowsArgs {
  body: IReportConfiguration;
  columnValues: ColumnValueSegment[][];
  rowIndex?: number;
  subdomain?: string;
  clientOptions?: RequestInit;
  parentQuery?: boolean;

  // paging
  limit?: number;
  offset?: number;

  // multigrouping
  groupingValues: string[];
}

export const getPivotRows = async ({
  body,
  columnValues,
  rowIndex,
  subdomain,
  clientOptions,
  parentQuery,
  limit,
  offset,
  groupingValues
}: IGetPivotRowsArgs): Promise<IOgqlV1JobResult> =>
  reportingService
    .options(clientOptions, true)
    .url(`/reports/${body.reportId}/pivot/rows`)
    .query({ entitySubdomain: subdomain })
    .post({ ...body, columnValues, rowIndex, parentQuery, limit, offset, groupingValues })
    .json<IOgqlV1QueryResult>()
    .then((results) => ({ id: uuidv4(), jobResult: results, state: EOgqlJobState.finished }));

export const getDetailTableData = async (
  reportId: string,
  reportConfiguration: IReportConfiguration,
  clientOptions?: RequestInit
): Promise<IOgqlV1JobResult> => {
  return reportingService
    .options(clientOptions, true)
    .url(`/reports/${reportId}/queries/detailTable`)
    .query({ useQueryService: true })
    .post(reportConfiguration)
    .json<IOgqlV1JobResult>();
};

interface IGetChartDataForMultiDatasetReportPayload {
  chartIndex: number;
  parentQuery: boolean;
  report: IAccountSummary;
  rowIndex: number;
  entitySubdomain: string;
  entityFiscalYearStartMonth: number;
  isServiceUser?: boolean;
}

export const getChartDataForMultiDatasetReport = async (
  payload: IGetChartDataForMultiDatasetReportPayload,
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<any> => {
  return reportingServiceNextOnHost(reportingServiceHost)
    .options(clientOptions, true)
    .url('/pivot/chart')
    .post(payload)
    .json();
};

export const getChartDataForSingleDatasetReport = async (
  reportId: string,
  payload: { reportConfiguration: IReportConfiguration },
  clientOptions?: RequestInit,
  reportingServiceHost?: string
): Promise<any> => {
  return reportingServiceV1OnHost(reportingServiceHost)
    .options(clientOptions, true)
    .url(`/reports/${reportId}/chart`)
    .post(payload)
    .json();
};

/**
 * Read-only preview of what a dataset swap would do. Unlike `validateReportDataset`, which answers a
 * single boolean over every column of the dataset, this reports only the columns the report actually
 * references, and how each one would be resolved.
 *
 * `reportConfigurationId` is the caller's current configuration. It is sent because the report's
 * `defaultReportConfiguration` pointer lags behind edits, so omitting it plans against stale columns.
 */
export const getDatasetChangePlan = async (
  reportId: string,
  datasetId: string,
  reportConfigurationId?: string,
  clientOptions?: RequestInit
): Promise<IDatasetChangePlanResponse> =>
  reportingService
    .options(clientOptions, true)
    .url(`/reports/${reportId}/dataset-change-plan`)
    .post({ datasetId, ...(reportConfigurationId && { reportConfigurationId }) })
    .json<IDatasetChangePlanResponse>()
    .catch(async (e) => {
      const error = e instanceof Error ? e : new Error(String(e));
      error.message = `getDatasetChangePlan: ${error.message}, reportId: ${reportId}`;
      return Promise.reject(error);
    });

/**
 * Applies the swap. The server re-derives the plan and rejects with 400 if the mapping does not
 * account for every unresolved reference, so the caller cannot skip the confirmation step.
 */
export const changeReportDataset = async (
  reportId: string,
  datasetId: string,
  mapping: IDatasetChangeMapping,
  reportConfigurationId?: string,
  clientOptions?: RequestInit
): Promise<IDatasetChangeApplyResponse> =>
  reportingService
    .options(clientOptions, true)
    .url(`/reports/${reportId}/change-dataset`)
    .post({ datasetId, mapping, ...(reportConfigurationId && { reportConfigurationId }) })
    .json<IDatasetChangeApplyResponse>()
    .catch(async (e) => {
      const error = e instanceof Error ? e : new Error(String(e));
      error.message = `changeReportDataset: ${error.message}, reportId: ${reportId}`;
      return Promise.reject(error);
    });
